import * as yup from "yup"
import React, { useEffect } from "react"
import SettingSection from "./setting-section"
import { User } from "lucide-react"
import { IconPassword, IconPasswordUser } from "@tabler/icons-react"
import { useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { useChangePasswordMutation } from "../../account/store/account.query"
import { useNavigate } from "react-router-dom"
import { Button, LoadingOverlay, PasswordInput, TextInput } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { Configuration } from "../../../models/configuration.model"
import {
  useLazyGetConfigurationsQuery,
  useUpdateConfigurationMutation,
} from "../store/settings.query"

const ConfigurationComponent = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [updateConfiguration, updateConfigurationResponse] =
    useUpdateConfigurationMutation()
  const [getConfigurations, configurations] = useLazyGetConfigurationsQuery()

  const schema = yup
    .object()
    .shape({
      gracePeriod: yup
        .number()
        .required(t("requiredValidation", { field: t("gracePeriod") })),

      lessThanTenUnitPrice: yup
        .number()
        .required(
          t("requiredValidation", { field: t("lessThanTenUnitPrice") }),
        ),

      lessThanTwentyUnitPrice: yup
        .number()
        .required(
          t("requiredValidation", { field: t("lessThanTwentyUnitPrice") }),
        ),

      lessThanThirtyUnitPrice: yup
        .number()
        .required(
          t("requiredValidation", { field: t("lessThanThirtyUnitPrice") }),
        ),

      lessThanFortyUnitPrice: yup
        .number()
        .required(
          t("requiredValidation", { field: t("lessThanFortyUnitPrice") }),
        ),

      lessThanFiftyUnitPrice: yup
        .number()
        .required(
          t("requiredValidation", { field: t("lessThanFiftyUnitPrice") }),
        ),
      lessThanSeventyUnitPrice: yup
        .number()
        .required(
          t("requiredValidation", { field: t("lessThanSeventyUnitPrice") }),
        ),
      lessThanHundredUnitPrice: yup
        .number()
        .required(
          t("requiredValidation", { field: t("lessThanHundredUnitPrice") }),
        ),

      greaterThanHundredUnitPrice: yup
        .number()
        .required(
          t("requiredValidation", { field: t("greaterThanHundredUnitPrice") }),
        ),
      annualPrice: yup
        .number()
        .required(t("requiredValidation", { field: t("annualPrice") })),
    })
    .required()

  const defaultValue = {
    gracePeriod: 0,
    lessThanTenUnitPrice: 0,
    lessThanTwentyUnitPrice: 0,
    lessThanThirtyUnitPrice: 0,
    lessThanFortyUnitPrice: 0,
    lessThanFiftyUnitPrice: 0,
    lessThanSeventyUnitPrice: 0,
    lessThanHundredUnitPrice: 0,
    greaterThanHundredUnitPrice: 0,
    annualPrice: 0,
  }

  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValue,
    mode: "all",
  })

  useEffect(() => {
    getConfigurations({ top: 10, skip: 0 }).then((configurations) => {
      //@ts-ignore
      reset(configurations.data as Configuration)
    })
  }, [])
  const onSubmit = (data: any) => {
    updateConfiguration(data)
  }

  const onError = (error: any) => {}
  return (
    <SettingSection icon={IconPasswordUser} title={t("configruation")}>
      <LoadingOverlay
        visible={
          updateConfigurationResponse.isLoading || configurations.isFetching
        }
      />
      <div className="flex flex-col sm:flex-row items-start mb-6">
        <form onSubmit={handleSubmit(onSubmit, onError)} className="w-full">
          <div className="flex gap-5 w-full">
            <TextInput
              classNames={{ label: "text-gray-200" }}
              className="w-full"
              type="number"
              {...register("gracePeriod")}
              label={t("gracePeriod")}
              placeholder={t("gracePeriod")}
            />
            <TextInput
              classNames={{ label: "text-gray-200" }}
              className="w-full"
              type="number"
              {...register("lessThanTenUnitPrice")}
              label={t("lessThanTenUnitPrice")}
              placeholder={t("lessThanTenUnitPrice")}
            />
          </div>
          <div className="flex gap-5 w-full my-4">
            <TextInput
              classNames={{ label: "text-gray-200" }}
              className="w-full"
              type="number"
              {...register("lessThanTwentyUnitPrice")}
              label={t("lessThanTwentyUnitPrice")}
              placeholder={t("lessThanTwentyUnitPrice")}
            />
            <TextInput
              classNames={{ label: "text-gray-200" }}
              className="w-full"
              type="number"
              {...register("lessThanThirtyUnitPrice")}
              label={t("lessThanThirtyUnitPrice")}
              placeholder={t("lessThanThirtyUnitPrice")}
            />
          </div>
          <div className="flex gap-5 w-full my-4">
            <TextInput
              classNames={{ label: "text-gray-200" }}
              className="w-full"
              type="number"
              {...register("lessThanFortyUnitPrice")}
              label={t("lessThanFortyUnitPrice")}
              placeholder={t("lessThanFortyUnitPrice")}
            />
            <TextInput
              classNames={{ label: "text-gray-200" }}
              className="w-full"
              type="number"
              {...register("lessThanFiftyUnitPrice")}
              label={t("lessThanFiftyUnitPrice")}
              placeholder={t("lessThanFiftyUnitPrice")}
            />
          </div>
          <div className="flex gap-5 w-full my-4">
            <TextInput
              classNames={{ label: "text-gray-200" }}
              className="w-full"
              type="number"
              {...register("lessThanSeventyUnitPrice")}
              label={t("lessThanSeventyUnitPrice")}
              placeholder={t("lessThanSeventyUnitPrice")}
            />
            <TextInput
              classNames={{ label: "text-gray-200" }}
              className="w-full"
              type="number"
              {...register("lessThanHundredUnitPrice")}
              label={t("lessThanHundredUnitPrice")}
              placeholder={t("lessThanHundredUnitPrice")}
            />
          </div>
          <div className="flex gap-5 w-full my-4">
            <TextInput
              classNames={{ label: "text-gray-200" }}
              className="w-full"
              type="number"
              {...register("greaterThanHundredUnitPrice")}
              label={t("greaterThanHundredUnitPrice")}
              placeholder={t("greaterThanHundredUnitPrice")}
            />
            <TextInput
              classNames={{ label: "text-gray-200" }}
              className="w-full"
              type="number"
              {...register("annualPrice")}
              label={t("annualPrice")}
              placeholder={t("annualPrice")}
            />
          </div>

          <Button
            variant="filled"
            className="bg-primary w-40 text-white mt-8"
            type="submit"
            classNames={{ label: "flex space-x-1" }}
            size={"xs"}
            loading={updateConfigurationResponse?.isLoading}
          >
            <span>{t("update")}</span>
          </Button>
        </form>
      </div>
      {/* <button className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded transition duration-200 w-full sm:w-auto">
                    Edit Profile
                </button> */}
    </SettingSection>
  )
}

export default ConfigurationComponent
